/**
 * Client-side evaluation of the rules engine's canonical condition AST (ConditionDto) against a
 * plain data record — the C7 checklist evaluates ConditionalRequiredFields policy conditions
 * against the CURRENT form data, so a field reads as required before the user submits.
 *
 * This is NOT a second operator semantics: it mirrors what AstToLambdaConverter emits and what
 * the RulesEngine then does with it, down to the null handling. Under C2's typed nulls the
 * converter null-GUARDS the families whose null has no comparable value (number, boolean, date),
 * so a null operand makes the comparison false — the rule fails, it does not blow up. A PRESENT
 * but non-convertible value ("abc" in a numeric field) still throws inside Convert.*, which the
 * server turns into a loud RuleEvaluationFailed / PolicyConditionEvaluationFailed — so this
 * evaluator throws there too, instead of guessing. String, enum and guid keep reading a null as ""
 * (the converter's NullSafeText), so `field == ""` remains the authored empty check.
 * A rule may flip that default per rule with `nullFieldBehavior: skip` (C2's knob, mirroring
 * nullRelationBehavior): the converter then emits `(x == null || cmp)` instead of `(x != null && cmp)`,
 * so a null operand in a guarded family makes the comparison HOLD — an open end is nothing to check.
 * Per leaf, never hoisted: an AND group still fails on its other leaves. The policy checklist never
 * sets it (a policy condition has no knob); business-rule callers pass the rule's declared value.
 * Parity is pinned by condition-evaluator-parity.fixture.json, which PolicyConditionParityTests.cs
 * runs through the real engine and conditionEvaluator.test.ts runs through this file.
 *
 * Scope (the same restriction the server enforces at authoring): leaves read the acting entity's
 * own fields ("Order.shippingMethod" or bare "shippingMethod") and compare against literal values
 * (the server rejects field-to-field references, which compile differently per runtime type).
 * Relation, aggregation, Tenant.* and Context.* paths throw.
 */
import type { ConditionDto } from '@/types/core/Rules';

export type ConditionData = Record<string, unknown>;

/** C2: what a null operand means in number/boolean/date comparisons — the rule's declared knob. */
export type NullFieldBehavior = 'fail' | 'skip';

export interface ConditionEvaluationOptions {
  /** Default 'fail' (the converter's null guard); 'skip' lets an open end hold. */
  nullFieldBehavior?: NullFieldBehavior;
}

export class ConditionEvaluationError extends Error {
  constructor(message: string, readonly nodeId?: string) {
    super(message);
    this.name = 'ConditionEvaluationError';
  }
}

type LeafNode = Extract<ConditionDto, { kind: 'rule' }>;
type ValueFamily = 'number' | 'boolean' | 'string' | 'enum' | 'date';
type ListFamily = 'number' | 'string' | 'enum';

const GUID_EMPTY = '00000000-0000-0000-0000-000000000000';

/**
 * The families AstToLambdaConverter null-guards (C2). Their null has no comparable value, so a
 * null operand short-circuits the emitted lambda to false; string/enum/guid read a null as ""
 * instead and need no guard.
 */
const GUARDED_FAMILIES: ReadonlySet<ValueFamily> = new Set(['number', 'boolean', 'date']);

export function evaluateCondition(
  condition: ConditionDto,
  entityName: string,
  data: ConditionData,
  options: ConditionEvaluationOptions = {}
): boolean {
  if (condition.kind === 'group') {
    return evaluateGroup(condition, entityName, data, options);
  }
  if (condition.kind === 'rule') {
    return evaluateLeaf(condition, entityName, data, options);
  }
  throw new ConditionEvaluationError(`Unknown condition kind '${(condition as { kind?: string }).kind}'.`);
}

function evaluateGroup(
  group: Extract<ConditionDto, { kind: 'group' }>,
  entityName: string,
  data: ConditionData,
  options: ConditionEvaluationOptions
): boolean {
  if (!group.children || group.children.length === 0) {
    throw new ConditionEvaluationError('Group condition must have at least one child.', group.nodeId);
  }
  const op = String(group.op).toUpperCase();
  if (op === 'AND') {
    return group.children.every((child) => evaluateCondition(child, entityName, data, options));
  }
  if (op === 'OR') {
    return group.children.some((child) => evaluateCondition(child, entityName, data, options));
  }
  throw new ConditionEvaluationError(`Group op must be AND or OR, got '${group.op}'.`, group.nodeId);
}

function evaluateLeaf(leaf: LeafNode, entityName: string, data: ConditionData, options: ConditionEvaluationOptions): boolean {
  const member = ownMember(leaf.field, entityName, leaf.nodeId);
  const lhs = readField(data, member);
  const nullHolds = options.nullFieldBehavior === 'skip';

  const nullCheck = evaluateNullCheck(leaf.operator, lhs);
  if (nullCheck !== undefined) {
    return nullCheck;
  }

  if (leaf.operator === 'in' || leaf.operator === 'notIn') {
    return evaluateList(leaf, lhs, nullHolds);
  }

  const rhs = leaf.value;
  const family = resolveScalarFamily(leaf, rhs);
  const verdictOnNull = nullShortCircuit(leaf, family, lhs, rhs, nullHolds);
  if (verdictOnNull !== undefined) {
    return verdictOnNull;
  }

  switch (family) {
    case 'number':
      return compareOrdered(leaf, toDecimal(lhs, leaf), toDecimal(rhs, leaf));
    case 'boolean':
      return compareEquality(leaf, toBoolean(lhs, leaf), toBoolean(rhs, leaf));
    case 'date':
      return compareOrdered(leaf, toDateMillis(lhs, leaf), toDateMillis(rhs, leaf));
    default:
      return compareEquality(leaf, nullAsEmpty(lhs), rhs === null || rhs === undefined ? null : String(rhs));
  }
}

/** "Order.shippingMethod" or "shippingMethod" → "shippingMethod"; anything else is not an own field. */
function ownMember(field: string, entityName: string, nodeId?: string): string {
  const parts = (field ?? '').split('.');
  if (parts.length === 1 && parts[0]) {
    return parts[0];
  }
  if (parts.length === 2 && parts[0].toLowerCase() === entityName.toLowerCase()) {
    return parts[1];
  }
  throw new ConditionEvaluationError(
    `'${field}' is not one of ${entityName}'s own fields; only own fields evaluate client-side.`,
    nodeId
  );
}

/** Reads a member case-insensitively; a flattened value-object key ("primaryQuantity_value") reads the nested component. */
export function readField(data: ConditionData, member: string): unknown {
  if (member in data) {
    return data[member];
  }
  const key = Object.keys(data).find((k) => k.toLowerCase() === member.toLowerCase());
  if (key !== undefined) {
    return data[key];
  }
  const underscore = member.indexOf('_');
  if (underscore > 0) {
    const parent = readField(data, member.slice(0, underscore));
    if (parent && typeof parent === 'object') {
      return readField(parent as ConditionData, member.slice(underscore + 1));
    }
  }
  return null;
}

function resolveScalarFamily(leaf: LeafNode, rhs: unknown): ValueFamily {
  const declared = leaf.valueType?.toLowerCase();
  switch (declared) {
    case 'number':
    case 'money':
    case 'integer':
    case 'decimal':
      return 'number';
    case 'boolean':
    case 'bool':
      return 'boolean';
    case 'date':
    case 'datetime':
      return 'date';
    case 'enum':
    case 'select':
    case 'multiselect':
      return 'enum';
    case 'string':
    case 'text':
      return 'string';
    case undefined:
    case null:
    case '':
      return inferFamily(rhs);
    default:
      throw new ConditionEvaluationError(`Unsupported value type '${leaf.valueType}'.`, leaf.nodeId);
  }
}

/** AstToLambdaConverter.InferValueType: numbers → number, booleans → boolean, everything else → string. */
function inferFamily(value: unknown): ValueFamily {
  if (typeof value === 'number') return 'number';
  if (typeof value === 'boolean') return 'boolean';
  return 'string';
}

function compareOrdered(leaf: LeafNode, lhs: number, rhs: number): boolean {
  switch (leaf.operator) {
    case '==': return lhs === rhs;
    case '!=': return lhs !== rhs;
    case '>': return lhs > rhs;
    case '>=': return lhs >= rhs;
    case '<': return lhs < rhs;
    case '<=': return lhs <= rhs;
    default:
      throw new ConditionEvaluationError(`Unsupported operator '${leaf.operator}'.`, leaf.nodeId);
  }
}

function compareEquality<T>(leaf: LeafNode, lhs: T, rhs: T): boolean {
  switch (leaf.operator) {
    case '==': return lhs === rhs;
    case '!=': return lhs !== rhs;
    default:
      throw new ConditionEvaluationError(
        `Operator '${leaf.operator}' is not supported for ${leaf.valueType ?? 'string'} values.`,
        leaf.nodeId
      );
  }
}

/**
 * in/notIn: the element family is pinned on the stored leaf at authoring (number/string/enum);
 * an unpinned list infers like the server's fallback — number only when every element IS a number.
 */
function evaluateList(leaf: LeafNode, lhs: unknown, nullHolds: boolean): boolean {
  const values = Array.isArray(leaf.value) ? (leaf.value as unknown[]) : null;
  if (!values || values.length === 0 || values.some((v) => v === null || v === undefined)) {
    throw new ConditionEvaluationError(
      `Operator '${leaf.operator}' requires a non-empty array of non-null literals.`,
      leaf.nodeId
    );
  }
  const family = resolveListFamily(leaf, values);

  // C2: the null guard wraps the WHOLE emitted chain, so a null member fails `notIn` too —
  // it never reads as "not one of these"; under nullFieldBehavior: skip the whole chain holds.
  if (family === 'number' && isNullish(lhs)) {
    return nullHolds;
  }

  const matches =
    family === 'number'
      ? matchesNumeric(leaf, lhs, values)
      : values.some((v) => String(v) === nullAsEmpty(lhs));
  return leaf.operator === 'in' ? matches : !matches;
}

/**
 * isNull / isNotNull: the member itself against null, exactly the converter's unwrapped
 * `field == null` / `field != null` — so an empty STRING is not null on either side, and a key the
 * form data does not carry (JSONForms drops cleared fields) reads as null, as the entity's null does.
 * Returns undefined for every other operator.
 */
function evaluateNullCheck(operator: string, lhs: unknown): boolean | undefined {
  if (operator === 'isNull') {
    return isNullish(lhs);
  }
  if (operator === 'isNotNull') {
    return !isNullish(lhs);
  }
  return undefined;
}

/**
 * C2 null handling, decided before any conversion. An explicit null literal is an IS NULL check
 * (the converter emits `field == null` with neither side wrapped), and a null operand in a guarded
 * family cannot satisfy the comparison — unless the rule declares nullFieldBehavior: skip, where
 * the guard is `(x == null || cmp)` and the null operand HOLDS. Returns undefined when the
 * comparison proceeds normally.
 */
function nullShortCircuit(
  leaf: LeafNode,
  family: ValueFamily,
  lhs: unknown,
  rhs: unknown,
  nullHolds: boolean
): boolean | undefined {
  if (isNullLiteral(leaf, family, rhs)) {
    return compareEquality(leaf, isNullish(lhs), true);
  }
  if (GUARDED_FAMILIES.has(family) && isNullish(lhs)) {
    return nullHolds;
  }
  return undefined;
}

/**
 * Mirrors AstToLambdaConverter.IsNullLiteral: a nullish literal — and an empty literal on anything
 * but a declared string/enum leaf — emits `field == null`, not a comparison against "".
 */
function isNullLiteral(leaf: LeafNode, family: ValueFamily, rhs: unknown): boolean {
  if (isNullish(rhs)) {
    return true;
  }
  if (rhs !== '') {
    return false;
  }
  const declared = leaf.valueType ? family : null;
  return declared !== 'string' && declared !== 'enum';
}

function isNullish(value: unknown): boolean {
  return value === null || value === undefined;
}

function resolveListFamily(leaf: LeafNode, values: unknown[]): ListFamily {
  const declared = leaf.valueType?.toLowerCase();
  if (declared === 'number' || declared === 'string' || declared === 'enum') {
    return declared;
  }
  if (declared === 'text') return 'string';
  if (declared === 'select' || declared === 'multiselect') return 'enum';
  return values.every((v) => typeof v === 'number') ? 'number' : 'string';
}

function matchesNumeric(leaf: LeafNode, lhs: unknown, values: unknown[]): boolean {
  const left = toDecimal(lhs, leaf);
  return values.some((v) => toDecimal(v, leaf) === left);
}

/** Convert.ToDecimal on a PRESENT value; a null never reaches here (the C2 guard short-circuits first). */
function toDecimal(value: unknown, leaf: LeafNode): number {
  if (typeof value === 'number' && !Number.isNaN(value)) {
    return value;
  }
  if (typeof value === 'boolean') {
    return value ? 1 : 0;
  }
  if (typeof value === 'string' && value.trim() !== '') {
    const parsed = Number(value.trim());
    if (!Number.isNaN(parsed)) {
      return parsed;
    }
  }
  throw new ConditionEvaluationError(
    `'${formatValue(value)}' is not a number (field '${leaf.field}'); an empty numeric field cannot be compared.`,
    leaf.nodeId
  );
}

/** Convert.ToBoolean on a PRESENT value: "" and unrecognised strings throw, exactly as the engine does. */
function toBoolean(value: unknown, leaf: LeafNode): boolean {
  if (typeof value === 'boolean') {
    return value;
  }
  if (typeof value === 'number') {
    return value !== 0;
  }
  if (typeof value === 'string') {
    const text = value.trim().toLowerCase();
    if (text === 'true') return true;
    if (text === 'false') return false;
  }
  throw new ConditionEvaluationError(
    `'${formatValue(value)}' is not a boolean (field '${leaf.field}'); an empty boolean field cannot be compared.`,
    leaf.nodeId
  );
}

/**
 * Mirrors the server's wall-clock comparison. Convert.ToDateTime yields DateTime values that
 * compare by their ticks with Kind ignored, so "2026-01-31" and "2026-01-31T00:00:00" denote the
 * same instant there. Date.parse does not agree with itself: a date-only string is UTC midnight
 * while an offset-less date-time is LOCAL midnight, which moved the boundary day by the browser's
 * offset and made the checklist disagree with the command handler on that day. A date-only string
 * is therefore read as local midnight, like every other offset-less value.
 */
const DATE_ONLY = /^\d{4}-\d{2}-\d{2}$/;

function asWallClock(text: string): string {
  return DATE_ONLY.test(text) ? `${text}T00:00:00` : text;
}

function toDateMillis(value: unknown, leaf: LeafNode): number {
  if (value instanceof Date) {
    return value.getTime();
  }
  if (typeof value === 'string' && value.trim() !== '') {
    const millis = Date.parse(asWallClock(value.trim()));
    if (!Number.isNaN(millis)) {
      return millis;
    }
  }
  throw new ConditionEvaluationError(
    `'${formatValue(value)}' is not a date (field '${leaf.field}'); an empty date field cannot be compared.`,
    leaf.nodeId
  );
}

/** AstToLambdaConverter.NullSafeText: a null member reads as "" for string, enum and guid. */
function nullAsEmpty(value: unknown): string {
  return value === null || value === undefined ? '' : String(value);
}

function formatValue(value: unknown): string {
  return value === null || value === undefined ? '' : String(value);
}

export const GUID_EMPTY_VALUE = GUID_EMPTY;
